import {
  ball,
  body,
  edge,
  fade,
  gloss,
  mix,
  n,
  shade,
  shadow,
  shadowInk,
  star,
  tint,
  type ArtPal,
} from './covers/kit';

/*
 * Neon Arcade (霓虹街机) symbols — an 80s arcade, no casino props (no 7s, cherries, bars, bells, chips, cards or $):
 * pixel star, pinball flipper with its ball, cassette, joystick, crystal, arcade UFO and the neon bolt (wild).
 * Same rules as ./symbols.ts (viewBox 0 0 100 100, readable at 48 px, no text, ids prefixed by `p`), plus a neon
 * look: every object has its volume (gradients, a gloss, a ground shadow) and a glowing tube along its outline —
 * a wide translucent halo, the tube and a pale core (no SVG filters: cheap to paint on the reels). The light theme
 * keeps the tubes but dims the halo. Only the wild sits on a plate (a tile with a neon rim, id `<p>r`).
 * Registered in symbols.ts (`...NEON_DRAW`), names prefixed `na` (raster group `neon-arcade`).
 */
type Draw = (P: ArtPal, p: string) => string;

const D = (...g: string[]) => `<defs>${g.join('')}</defs>`;
const fl = (p: string, id: string) => `fill="url(#${p}${id})"`;
const drop = (P: ArtPal, p: string) => fade(`${p}s`, shadowInk(P), P.dark ? 0.6 : 0.24);
const ground = (p: string, cx = 50, cy = 91, rx = 28, ry = 5) => shadow(`${p}s`, cx, cy, rx, ry);
const ol = (c: string, P: ArtPal, w: number | string = 2) =>
  `stroke="${edge(c, P)}" stroke-width="${w}" stroke-linejoin="round" stroke-linecap="round"`;
const round = 'stroke-linejoin="round" stroke-linecap="round"';

/** neon colour of a tube: bright on dark, deeper on light (it must read on a pale reel) */
const neon = (c: string, P: ArtPal) => (P.dark ? tint(c, 0.12) : shade(c, 0.08));
/** the body behind a tube: a dark glassy tint on dark, a pale tint on light */
const glassOf = (c: string, P: ArtPal) => (P.dark ? mix(c, P.bg, 0.55) : tint(c, 0.72));

/** A glowing neon tube along `d`: halo, tube, pale core. */
function tube(d: string, c: string, P: ArtPal, w = 3): string {
  const t = neon(c, P);
  return (
    `<path d="${d}" fill="none" stroke="${t}" stroke-opacity="${P.dark ? 0.3 : 0.16}" stroke-width="${n(w * 3.2)}" ${round}/>` +
    `<path d="${d}" fill="none" stroke="${t}" stroke-width="${n(w)}" ${round}/>` +
    `<path d="${d}" fill="none" stroke="${tint(t, P.dark ? 0.75 : 0.6)}" stroke-opacity=".9" stroke-width="${n(w * 0.36)}" ${round}/>`
  );
}

/** soft coloured glow behind an object (a neon light pool) */
const halo = (p: string, id: string, c: string, P: ArtPal) => fade(`${p}${id}`, c, P.dark ? 0.5 : 0.28);

/** 8-bit star: 11 × 11 pixels */
const STAR_ROWS = [
  '.....X.....',
  '.....X.....',
  '....XXX....',
  '....XXX....',
  'XXXXXXXXXXX',
  '.XXXXXXXXX.',
  '..XXXXXXX..',
  '...XXXXX...',
  '..XXX.XXX..',
  '.XXX...XXX.',
  'XX.......XX',
];

/** pixel star: chunky 8-bit blocks with a lit edge, two pixel sparkles */
const naStar: Draw = (P, p) => {
  const c = P.dark ? P.gold : mix(tint(P.gold, 0.3), '#FFD166', 0.55);
  const px = 7;
  const x0 = 11.5;
  const y0 = 8;
  let blocks = '';
  let lit = '';
  STAR_ROWS.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      if (ch !== 'X') return;
      blocks += `M${n(x0 + x * px)} ${n(y0 + y * px)}h${px}v${px}h-${px}Z`;
      // the top-left edge of the shape catches the light
      if (row[x - 1] !== 'X' || STAR_ROWS[y - 1]?.[x] !== 'X')
        lit += `M${n(x0 + x * px + 1)} ${n(y0 + y * px + 1)}h2.4v2.4h-2.4Z`;
    }),
  );
  const sparkle = (x: number, y: number, col: string) =>
    `<path d="M${x} ${y - 6}V${y + 6}M${x - 6} ${y}H${x + 6}" stroke="${neon(col, P)}" stroke-width="2.4" stroke-linecap="square"/>`;
  return (
    D(drop(P, p), halo(p, 'h', c, P), body(`${p}b`, c, 0.45, 0.35, 0.5, 1)) +
    `<circle cx="50" cy="48" r="42" fill="url(#${p}h)"/>` +
    ground(p, 50, 92, 30, 4) +
    `<path d="${blocks}" fill="none" stroke="${neon(c, P)}" stroke-opacity="${P.dark ? 0.3 : 0.18}" stroke-width="7" ${round}/>` +
    `<path d="${blocks}" ${fl(p, 'b')} stroke="${edge(c, P)}" stroke-opacity=".35" stroke-width=".7"/>` +
    `<path d="${lit}" fill="#FFFFFF" fill-opacity=".75"/>` +
    sparkle(14, 18, P.pink) +
    sparkle(86, 22, P.teal)
  );
};

/** pinball flipper with its rubber edge glowing pink, a steel ball above and two speed streaks */
const naFlipper: Draw = (P, p) => {
  const c = P.pink;
  const steel = P.dark ? '#DDE4F0' : '#C9D2E0';
  const flip = 'M26 58C26 50 33 46 40 47L83 57C88 58 88 65 83 66L40 75C33 77 26 69 26 62Z';
  return (
    D(
      drop(P, p),
      halo(p, 'h', c, P),
      body(`${p}b`, glassOf(c, P), 0.35, 0.35, 0.6, 1),
      ball(`${p}k`, steel, 0.5, 0.45),
      ball(`${p}v`, P.dark ? P.slate : '#9AA6BA', 0.4, 0.4),
    ) +
    `<ellipse cx="54" cy="58" rx="40" ry="30" fill="url(#${p}h)"/>` +
    ground(p, 52, 90, 32, 4) +
    `<g transform="translate(-6 4) scale(1.12) rotate(-14 34 62)">` +
    `<path d="${flip}" ${fl(p, 'b')} ${ol(c, P, 1.4)}/>` +
    tube(flip, c, P, 3.4) +
    `<circle cx="36" cy="61" r="6.5" fill="url(#${p}v)" ${ol(steel, P, 1.4)}/>` +
    `<circle cx="36" cy="61" r="2" fill="${P.dark ? '#FFFFFF' : '#EEF1F6'}"/>` +
    `</g>` +
    `<path d="M40 30H22M42 22H30M38 38H26" stroke="${neon(P.teal, P)}" stroke-width="2.6" stroke-linecap="round" stroke-opacity=".85"/>` +
    `<circle cx="61" cy="27" r="13" fill="url(#${p}k)" ${ol(steel, P, 1.6)}/>` +
    gloss(56, 22, 5, 3, -35, 0.85)
  );
};

/** audio cassette: a sky-blue neon shell, a pink-orange label, two spools in the window, the tape guide below */
const naCassette: Draw = (P, p) => {
  const c = P.sky;
  const label = P.dark ? mix(P.pink, P.orange, 0.4) : mix(tint(P.pink, 0.2), P.orange, 0.35);
  const shell = 'M20 22H80Q88 22 88 30V72Q88 80 80 80H20Q12 80 12 72V30Q12 22 20 22Z';
  const win = P.dark ? shade(P.bg, 0.2) : '#2B3445';
  const spool = (x: number) =>
    `<circle cx="${x}" cy="47" r="7" fill="${P.dark ? '#EEF1F8' : '#FFFFFF'}" ${ol('#C9D2E0', P, 1.2)}/>` +
    `<path d="M${x - 3.5} 47H${x + 3.5}M${x} 43.5V50.5" stroke="${win}" stroke-width="2"/>`;
  return (
    D(
      drop(P, p),
      halo(p, 'h', c, P),
      body(`${p}b`, glassOf(c, P), 0.3, 0.35, 0.6, 1),
      body(`${p}l`, label, 0.35, 0.25, 1, 0.3),
      `<linearGradient id="${p}t" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${shade(c, 0.5)}"/><stop offset="1" stop-color="${shade(c, 0.2)}"/></linearGradient>`,
    ) +
    `<ellipse cx="50" cy="51" rx="46" ry="38" fill="url(#${p}h)"/>` +
    ground(p, 50, 91, 34, 4) +
    `<path d="${shell}" ${fl(p, 'b')} ${ol(c, P, 1.4)}/>` +
    `<rect x="20" y="29" width="60" height="30" rx="4" fill="url(#${p}l)" ${ol(label, P, 1.4)}/>` +
    `<path d="M24 34H58" stroke="#FFFFFF" stroke-opacity=".6" stroke-width="2" stroke-linecap="round"/>` +
    `<rect x="30" y="39" width="40" height="16" rx="8" fill="${win}"/>` +
    `<path d="M43 47H57" stroke="url(#${p}t)" stroke-width="5"/>` +
    spool(38) +
    spool(62) +
    `<path d="M28 80L32 66H68L72 80" fill="${shade(glassOf(c, P), 0.15)}" ${ol(c, P, 1.4)}/>` +
    `<circle cx="38" cy="73" r="2.4" fill="${win}"/><circle cx="62" cy="73" r="2.4" fill="${win}"/>` +
    tube(shell, c, P, 3) +
    gloss(24, 28, 6, 2.4, -10, 0.55)
  );
};

/** arcade joystick: a purple neon base with two buttons, a steel stick and a red ball top */
const naJoystick: Draw = (P, p) => {
  const c = P.purple;
  const knob = P.red;
  const base = 'M14 66Q14 58 22 58H78Q86 58 86 66V78Q86 86 78 86H22Q14 86 14 78Z';
  const steel = P.dark ? '#DDE4F0' : '#B9C2D2';
  return (
    D(
      drop(P, p),
      halo(p, 'h', c, P),
      body(`${p}b`, glassOf(c, P), 0.35, 0.35, 0.6, 1),
      `<linearGradient id="${p}st" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${shade(steel, 0.35)}"/><stop offset=".4" stop-color="${tint(steel, 0.6)}"/><stop offset="1" stop-color="${shade(steel, 0.4)}"/></linearGradient>`,
      ball(`${p}k`, knob, 0.5, 0.4),
      ball(`${p}t`, P.teal, 0.5, 0.35),
      ball(`${p}y`, P.gold, 0.5, 0.35),
    ) +
    `<ellipse cx="50" cy="58" rx="44" ry="36" fill="url(#${p}h)"/>` +
    ground(p, 50, 92, 36, 4) +
    `<path d="${base}" ${fl(p, 'b')} ${ol(c, P, 1.4)}/>` +
    tube(base, c, P, 3) +
    `<ellipse cx="50" cy="64" rx="10" ry="3.6" fill="${shade(glassOf(c, P), 0.35)}"/>` +
    `<path d="M48 64L44 30H51L52 64Z" fill="url(#${p}st)" ${ol(steel, P, 1.2)}/>` +
    `<circle cx="47" cy="24" r="14" fill="url(#${p}k)" ${ol(knob, P, 1.8)}/>` +
    gloss(42, 18, 5, 3.4, -35, 0.8) +
    `<circle cx="27" cy="72" r="5" fill="url(#${p}t)" ${ol(P.teal, P, 1.2)}/>` +
    `<circle cx="73" cy="72" r="5" fill="url(#${p}y)" ${ol(P.gold, P, 1.2)}/>`
  );
};

/** faceted crystal: a teal gem with lit and shaded facets, a neon outline and a twinkle */
const naCrystal: Draw = (P, p) => {
  const c = P.teal;
  const g = P.dark ? c : mix(c, P.sky, 0.2);
  const outline = 'M16 42L30 24H70L84 42L50 88Z';
  const facet = (d: string, col: string) => `<path d="${d}" fill="${col}"/>`;
  return (
    D(drop(P, p), halo(p, 'h', c, P), body(`${p}b`, g, 0.3, 0.4, 0.6, 1)) +
    `<circle cx="50" cy="52" r="44" fill="url(#${p}h)"/>` +
    ground(p, 50, 93, 22, 3.5) +
    `<path d="${outline}" ${fl(p, 'b')}/>` +
    facet('M30 24L38 42H16Z', tint(g, 0.45)) +
    facet('M30 24H50L38 42Z', tint(g, 0.6)) +
    facet('M50 24H70L62 42Z', tint(g, 0.2)) +
    facet('M70 24L84 42H62Z', shade(g, 0.15)) +
    facet('M38 42H62L50 88Z', tint(g, 0.25)) +
    facet('M62 42H84L50 88Z', shade(g, 0.3)) +
    `<path d="M16 42H84M38 42L50 24L62 42M38 42L50 88L62 42" fill="none" stroke="${tint(g, 0.7)}" stroke-opacity=".7" stroke-width="1.2" ${round}/>` +
    tube(outline, c, P, 2.8) +
    `<polygon points="${star(76, 18, 8, 2, 4)}" fill="${P.dark ? '#FFFFFF' : neon(P.sky, P)}" fill-opacity=".95"/>` +
    gloss(30, 32, 6, 2.4, -50, 0.75)
  );
};

/** arcade UFO: a purple saucer with a glass dome, five gold lights on its rim and a teal tractor beam */
const naUfo: Draw = (P, p) => {
  const c = P.purple;
  const glass = P.sky;
  const beam = P.teal;
  const saucer = 'M12 58C12 50 30 44 50 44C70 44 88 50 88 58C88 66 70 72 50 72C30 72 12 66 12 58Z';
  const lights = [24, 37, 50, 63, 76]
    .map((x, i) => `<circle cx="${x}" cy="${i === 0 || i === 4 ? 61 : 64}" r="3.2" fill="url(#${p}y)"/>`)
    .join('');
  return (
    D(
      drop(P, p),
      halo(p, 'h', c, P),
      body(`${p}b`, P.dark ? mix(c, P.bg, 0.35) : tint(c, 0.45), 0.4, 0.4, 0.5, 1),
      ball(`${p}d`, glass, 0.6, 0.3),
      ball(`${p}y`, P.gold, 0.6, 0.25),
      `<linearGradient id="${p}t" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${neon(beam, P)}" stop-opacity="${P.dark ? 0.55 : 0.4}"/><stop offset="1" stop-color="${neon(beam, P)}" stop-opacity="0"/></linearGradient>`,
    ) +
    `<ellipse cx="50" cy="52" rx="46" ry="34" fill="url(#${p}h)"/>` +
    `<path d="M38 70L24 96H76L62 70Z" fill="url(#${p}t)"/>` +
    `<path d="M50 30V18" stroke="${neon(P.pink, P)}" stroke-width="2.4" stroke-linecap="round"/><circle cx="50" cy="16" r="3.4" fill="${neon(P.pink, P)}"/>` +
    `<path d="M30 48C30 34 39 26 50 26C61 26 70 34 70 48Z" fill="url(#${p}d)" ${ol(glass, P, 1.6)}/>` +
    `<path d="${saucer}" ${fl(p, 'b')} ${ol(c, P, 1.4)}/>` +
    `<path d="M16 56C30 52 70 52 84 56" fill="none" stroke="${tint(c, 0.5)}" stroke-opacity=".7" stroke-width="1.6"/>` +
    tube(saucer, c, P, 3) +
    lights +
    gloss(40, 34, 6, 3.4, -35, 0.8) +
    gloss(28, 51, 7, 2, -6, 0.45)
  );
};

/** the wild: a neon lightning bolt on a dark tile with a pink → purple → sky neon rim (the plate of a special) */
const naBolt: Draw = (P, p) => {
  const c = P.dark ? P.gold : mix(P.gold, P.orange, 0.25);
  const bolt = 'M58 12L26 54H46L38 88L74 42H54L64 12Z';
  const tile = P.dark ? mix(P.purple, P.bg, 0.78) : tint(P.purple, 0.82);
  return (
    D(
      fade(`${p}g`, P.pink, P.dark ? 0.6 : 0.4),
      body(`${p}m`, tile, P.dark ? 0.15 : 0.4, 0.35, 0.6, 1),
      `<linearGradient id="${p}r" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${neon(P.pink, P)}"/><stop offset=".5" stop-color="${neon(P.purple, P)}"/><stop offset="1" stop-color="${neon(P.sky, P)}"/></linearGradient>`,
      body(`${p}b`, c, 0.6, 0.25, 0.6, 1),
      halo(p, 'h', c, P),
    ) +
    `<circle cx="50" cy="50" r="50" fill="url(#${p}g)"/>` +
    `<rect x="7" y="7" width="86" height="86" rx="20" fill="url(#${p}m)" stroke="url(#${p}r)" stroke-opacity="${P.dark ? 0.35 : 0.25}" stroke-width="10"/>` +
    `<rect x="7" y="7" width="86" height="86" rx="20" fill="none" stroke="url(#${p}r)" stroke-width="3.6"/>` +
    `<rect x="12" y="12" width="76" height="76" rx="16" fill="none" stroke="#FFFFFF" stroke-opacity="${P.dark ? 0.14 : 0.6}" stroke-width="1.2"/>` +
    `<circle cx="50" cy="50" r="34" fill="url(#${p}h)"/>` +
    tube(bolt, c, P, 4) +
    `<path d="${bolt}" ${fl(p, 'b')} ${ol(c, P, 1.4)}/>` +
    `<path d="M56 18L34 50" stroke="#FFFFFF" stroke-opacity=".75" stroke-width="2.4" stroke-linecap="round"/>` +
    gloss(28, 22, 12, 4, -28, P.dark ? 0.16 : 0.5)
  );
};

/** Neon Arcade symbols for the shared registry (symbols.ts), highest pay last like the paytable reversed. */
export const NEON_DRAW = {
  naStar,
  naFlipper,
  naCassette,
  naJoystick,
  naCrystal,
  naUfo,
  naBolt,
} satisfies Record<string, Draw>;
